import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Facebook, CheckCircle2, Heart, Music, Users, Award } from "lucide-react";
import Reveal from "../components/Reveal";
import { IMAGES, COMMITMENTS } from "../mock/mock";

const ICONS = { Heart, Music, Users, Award };

const About = () => {
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="pt-40 pb-20 px-5 text-center relative">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[500px] h-[400px] bg-[#e0b04c]/8 rounded-full blur-[130px] pointer-events-none" />
        <Reveal className="relative z-10 max-w-3xl mx-auto">
          <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-5">Our Story</p>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[#f5f0e8] leading-tight">
            Honoring a Legacy, <span className="text-gold-gradient">Note by Note</span>
          </h1>
          <p className="text-[#b3a998] text-lg leading-relaxed mt-7 max-w-2xl mx-auto">
            A.T.O.M. was created by musicians who grew up with the timeless
            grooves of "Before I Let Go," "Joy and Pain," and "Golden Time of
            Day" as the soundtrack to their lives.
          </p>
        </Reveal>
      </section>

      {/* IMAGE */}
      <section className="px-5 pb-20">
        <Reveal className="max-w-5xl mx-auto">
          <img src={IMAGES.seeYouSoon} alt="A.T.O.M. band performing" className="rounded-2xl w-full object-cover shadow-2xl" />
        </Reveal>
      </section>

      {/* WHY */}
      <section className="py-20 px-5">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5f0e8] mb-8">
              Why <span className="text-gold-gradient">A.T.O.M.?</span>
            </h2>
            <p className="text-[#b3a998] leading-relaxed mb-5 text-lg">
              The music of Frankie Beverly &amp; Maze isn't just music—it's a
              generational bond. It's the song that played at your parents'
              wedding. It's the groove that gets the whole cookout dancing. It's
              the anthem that reminds us of love, resilience, and joy.
            </p>
            <p className="text-[#b3a998] leading-relaxed mb-5 text-lg">
              A.T.O.M. (A Tribute to Maze) was born from our collective desire to
              keep this music alive and accessible. We are not imitators—we are
              celebrants. Every performance is our way of saying "thank you" to
              Frankie Beverly for the gift he gave us all.
            </p>
            <p className="text-[#b3a998] leading-relaxed text-lg">
              Our members bring decades of experience in soul, R&B, funk, and
              gospel. We've performed at festivals, theaters, casinos, corporate
              events, and private celebrations across the country. We know how to
              read a room, build a setlist, and leave an audience wanting more.
            </p>
          </Reveal>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="py-20 px-5 bg-[#0a0806]/60 border-y border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <a href="https://www.facebook.com/groups/431810379580661" target="_blank" rel="noopener noreferrer" className="block group relative">
              <div className="absolute -inset-2 bg-[#e0b04c]/15 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <img src={IMAGES.facebookPreview} alt="A.T.O.M. Facebook page" className="relative rounded-xl w-full object-cover shadow-xl group-hover:scale-[1.02] transition-transform duration-500" />
            </a>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-4">Stay Connected</p>
            <h2 className="font-serif-display text-4xl text-[#f5f0e8] mb-6">Join the Community</h2>
            <p className="text-[#b3a998] leading-relaxed mb-6">
              If you've been rocking with us—or you've been meaning to catch a
              show—this is where you stay connected. This isn't just a page—it's
              the community behind the music.
            </p>
            <ul className="space-y-3 mb-8">
              {["Upcoming shows and tour stops", "Behind-the-scenes moments", "Music, videos, and live clips", "Real-time updates you don't want to miss"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[#c9bda9]">
                  <CheckCircle2 size={18} className="text-[#e0b04c] flex-shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <a href="https://www.facebook.com/groups/431810379580661" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gradient-to-b from-[#e8bc55] to-[#c99339] text-[#0c0a08] font-semibold px-7 py-3.5 rounded-md hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(224,176,76,0.35)] transition-all duration-300">
              <Facebook size={18} /> Follow. Engage. Be part of it.
            </a>
          </Reveal>
        </div>
      </section>

      {/* COMMITMENTS */}
      <section className="py-24 px-5">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-14">
            <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-4">What We Stand For</p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5f0e8]">Our Commitment</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {COMMITMENTS.map((c, i) => {
              const Icon = ICONS[c.icon];
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="h-full p-8 rounded-xl bg-[#12100c] border border-white/5 hover:border-[#e0b04c]/30 transition-all duration-400 flex gap-5">
                    <div className="w-14 h-14 rounded-lg bg-[#e0b04c]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="text-[#e0b04c]" size={26} />
                    </div>
                    <div>
                      <h3 className="font-serif-display text-2xl text-[#f5f0e8] mb-2">{c.title}</h3>
                      <p className="text-[#9c917f] leading-relaxed">{c.text}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-5 bg-[#0a0806]/60 border-t border-white/5">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <img src={IMAGES.allWhiteTribute} alt="A.T.O.M. all-white tribute" className="rounded-xl w-full object-cover shadow-xl" />
          </Reveal>
          <Reveal delay={150}>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5f0e8] mb-6 leading-tight">
              Ready to Experience the <span className="text-gold-gradient">Difference?</span>
            </h2>
            <p className="text-[#b3a998] leading-relaxed mb-8">
              Let's discuss how A.T.O.M. can bring the magic of Frankie Beverly &amp;
              Maze to your next event.
            </p>
            <Link to="/booking" className="inline-flex items-center gap-2 bg-gradient-to-b from-[#e8bc55] to-[#c99339] text-[#0c0a08] font-semibold px-8 py-4 rounded-md hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(224,176,76,0.4)] transition-all duration-300">
              Book A.T.O.M. <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
